import React, { useEffect } from 'react';

// Formik
import { useFormikField } from 'helpers/useFormikField';
import { FormikProps } from 'formik';

// Yup
import { reach } from 'yup';
import { addressSchema } from 'yupSchemas/addressSchema';

// Material
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';

// Helpers
import { useDebouncedSearch } from 'helpers/debounceHook';

// Services
import searchCep from 'services/apiSearchCep';

// Components
import { MaskedInputCEP } from 'components/MaskedInputs';

// Constants
import STATE_LIST from 'constants/states';

// Styles
import useStyles from './formAddress.styles';

export interface AddressInterface {
  cep: string;
  street: string;
  number: string;
  compl: string;
  neigh: string;
  city: string;
  state: string;
}

interface AddressFormProps {
  value?: AddressInterface;
  fieldName: string; // formik address field
  formik?: FormikProps<any>;
  setCep?: (param: string) => void;
  className?: string;
  disabled?: boolean;
  withMargin?: boolean;
  textAlign?: boolean;
  shrink?: boolean;
  spacing?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
}

type Props = AddressFormProps;

const useSearchCep = () => useDebouncedSearch(searchCep);

const AddressForm: React.FC<Props> = ({
  fieldName,
  formik,
  className,
  disabled,
  withMargin,
  textAlign,
  shrink,
  spacing,
  value,
  setCep,
}) => {
  const classes = useStyles();

  const [field, meta, helpers] = useFormikField<AddressInterface>(fieldName);
  const [cepFieldText, setCepFieldText, searchResults] = useSearchCep();
  const cepIsValid = async (cep: string) => {
    const valid = await reach(addressSchema, 'cep').isValid(cep);
    return valid;
  };
  const handleChangeCep = async (event: any) => {
    field.onChange(event);
    const valid = await cepIsValid(event.target.value);
    if (valid) {
      if (setCep) setCep(event.target.value);
      setCepFieldText(event.target.value);
    }
  };
  const handleChange = (e: React.ChangeEvent<any>, key: string) => {
    if (formik) {
      if (formik.values.address) {
        formik.setFieldValue('address', {
          ...formik.values.address,
          [key]: e.target.value,
        });
      } else {
        formik.setValues({
          ...formik.values,
          [key]: e.target.value,
        });
      }

      helpers.setValue({
        ...formik.values.address,
        [key]: e.target.value,
      });
      return;
    }
    field.onChange(e);
  };

  const setAddressData = (data: any) => {
    const { bairro, cidade, endereco, uf, cep } = data;

    if (value) {
      helpers.setValue({
        ...value,
        state: uf,
        city: cidade,
        neigh: bairro,
        street: endereco,
        cep,
      });
    } else {
      if (formik) {
        formik.setFieldValue('address', {
          ...field.value,
          state: uf,
          city: cidade,
          neigh: bairro,
          street: endereco,
          cep,
        });
      }
      helpers.setValue({
        ...field.value,
        state: uf,
        city: cidade,
        neigh: bairro,
        street: endereco,
        cep,
      });
    }
  };
  useEffect(() => {
    if (!searchResults.loading && searchResults.result) {
      const { status, data } = searchResults.result;
      if (status === 200 && !data.error) {
        setAddressData(data);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults.result, searchResults.loading]);

  return (
    <Grid container direction='row' spacing={spacing}>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <TextField
          disabled={disabled}
          className={className || ''}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: shrink || false,
                }
              : { shrink: shrink || false }
          }
          name={`${fieldName}.cep`}
          label='CEP'
          fullWidth
          variant='outlined'
          value={field.value?.cep || value?.cep}
          onBlur={field.onBlur}
          onChange={handleChangeCep}
          margin={withMargin ? 'normal' : 'none'}
          InputProps={{ inputComponent: MaskedInputCEP as any }}
          error={Boolean(
            meta.touched && meta.error && meta.error.cep && meta.touched.cep,
          )}
          helperText={
            meta.touched &&
            meta.error &&
            meta.error.cep &&
            meta.touched.cep &&
            meta.error.cep
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6} />
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <TextField
          disabled={disabled}
          className={className || ''}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: shrink || false,
                }
              : { shrink: shrink || false }
          }
          name={`${fieldName}.street`}
          label='Rua'
          fullWidth
          variant='outlined'
          value={value?.street || field.value?.street}
          onBlur={field.onBlur}
          onChange={(e) => handleChange(e, 'street')}
          margin={withMargin ? 'normal' : 'none'}
          error={Boolean(
            meta.touched &&
              meta.error &&
              meta.error.street &&
              meta.touched.street,
          )}
          helperText={
            meta.touched &&
            meta.error &&
            meta.error.street &&
            meta.touched.street &&
            meta.error.street
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <TextField
          classes={{
            root: classes.inputRoot,
          }}
          className={className || ''}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: shrink || false,
                }
              : { shrink: shrink || false }
          }
          disabled={disabled}
          name={`${fieldName}.number`}
          label='Número'
          fullWidth
          variant='outlined'
          type='number'
          value={value?.number || field.value?.number}
          onBlur={field.onBlur}
          onChange={(e) => handleChange(e, 'number')}
          margin={withMargin ? 'normal' : 'none'}
          error={Boolean(
            meta.touched &&
              meta.error &&
              meta.error.number &&
              meta.touched.number,
          )}
          helperText={
            meta.touched &&
            meta.error &&
            meta.error.number &&
            meta.touched.number &&
            meta.error.number
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <TextField
          disabled={disabled}
          className={className || ''}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: shrink || false,
                }
              : { shrink: shrink || false }
          }
          name={`${fieldName}.compl`}
          label='Complemento'
          fullWidth
          variant='outlined'
          margin={withMargin ? 'normal' : 'none'}
          value={value?.compl || field.value?.compl}
          onBlur={field.onBlur}
          onChange={(e) => handleChange(e, 'compl')}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <TextField
          disabled={disabled}
          className={className || ''}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: shrink || false,
                }
              : { shrink: shrink || false }
          }
          name={`${fieldName}.neigh`}
          label='Bairro'
          fullWidth
          variant='outlined'
          value={value?.neigh || field.value?.neigh}
          onBlur={field.onBlur}
          onChange={(e) => handleChange(e, 'neigh')}
          margin={withMargin ? 'normal' : 'none'}
          error={Boolean(
            meta.touched &&
              meta.error &&
              meta.error.neigh &&
              meta.touched.neigh,
          )}
          helperText={
            meta.touched &&
            meta.error &&
            meta.error.neigh &&
            meta.touched.neigh &&
            meta.error.neigh
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <TextField
          disabled={disabled}
          className={className || ''}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: shrink || false,
                }
              : { shrink: shrink || false }
          }
          name={`${fieldName}.city`}
          label='Cidade'
          fullWidth
          variant='outlined'
          value={value?.city || field.value?.city}
          onBlur={field.onBlur}
          onChange={(e) => handleChange(e, 'city')}
          margin={withMargin ? 'normal' : 'none'}
          error={Boolean(
            meta.touched && meta.error && meta.error.city && meta.touched.city,
          )}
          helperText={
            meta.touched &&
            meta.error &&
            meta.error.city &&
            meta.touched.city &&
            meta.error.city
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <TextField
          disabled={disabled}
          className={className || ''}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: shrink || false,
                }
              : { shrink: shrink || false }
          }
          name={`${fieldName}.state`}
          label='Estado'
          select
          fullWidth
          variant='outlined'
          value={value?.state || field.value?.state}
          onBlur={field.onBlur}
          onChange={(e) => handleChange(e, 'state')}
          margin={withMargin ? 'normal' : 'none'}
          error={Boolean(
            meta.touched &&
              meta.error &&
              meta.error.state &&
              meta.touched.state,
          )}
          helperText={
            meta.touched &&
            meta.error &&
            meta.error.state &&
            meta.touched.state &&
            meta.error.state
          }>
          {STATE_LIST.map((state) => (
            <MenuItem key={state.abbreviation} value={state.abbreviation}>
              {state.abbreviation}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );
};

export { AddressForm };
export default AddressForm;
